import React, { useEffect, useState } from 'react';
import { Quote } from '../types/quote';
import { getQuotesForThread, createQuote, withdrawQuote, acceptQuote, declineQuote } from '../api/quote';
import QuoteBuilder from './QuoteBuilder';
import QuoteSummary from './QuoteSummary';

interface QuotePanelProps {
  jobId: number;
  contractorProfileId: number;
  jobStatus: string;
  isContractorSide: boolean;
  isClientSide: boolean;
  onAccepted: () => void;
}

const REVISABLE_STATUSES = new Set(['Declined', 'Withdrawn', 'Superseded']);

const QuotePanel: React.FC<QuotePanelProps> = ({ jobId, contractorProfileId, jobStatus, isContractorSide, isClientSide, onAccepted }) => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState('');
  const [showDeclineInput, setShowDeclineInput] = useState(false);

  const refresh = () => {
    setLoading(true);
    getQuotesForThread(jobId, contractorProfileId)
      .then(setQuotes)
      .catch(() => setQuotes([]))
      .finally(() => setLoading(false));
  };

  useEffect(refresh, [jobId, contractorProfileId]);

  if (!isContractorSide && !isClientSide) return null;
  if (loading) return null;

  const latest = quotes.length > 0 ? quotes[quotes.length - 1] : undefined;

  const handleCreate = async () => {
    setBusy(true);
    setError(null);
    try {
      await createQuote(jobId);
      refresh();
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to create Quote.');
    } finally {
      setBusy(false);
    }
  };

  const handleWithdraw = async () => {
    if (!latest) return;
    setBusy(true);
    setError(null);
    try {
      await withdrawQuote(latest.id);
      refresh();
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to withdraw Quote.');
    } finally {
      setBusy(false);
    }
  };

  const handleAccept = async () => {
    if (!latest) return;
    setBusy(true);
    setError(null);
    try {
      await acceptQuote(latest.id);
      refresh();
      onAccepted();
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to accept Quote.');
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!latest) return;
    setBusy(true);
    setError(null);
    try {
      await declineQuote(latest.id, declineReason.trim() || undefined);
      setShowDeclineInput(false);
      setDeclineReason('');
      refresh();
    } catch (e: any) {
      setError(e.response?.data ?? e.message ?? 'Failed to decline Quote.');
    } finally {
      setBusy(false);
    }
  };

  // Contractor side
  if (isContractorSide) {
    if (!latest || REVISABLE_STATUSES.has(latest.status)) {
      if (jobStatus !== 'Open') return null;
      return (
        <div className="p-4 bg-surface-container-low border-b border-outline-variant flex items-center justify-between gap-3">
          <p className="text-sm text-on-surface-variant">
            {latest ? `Your previous Quote was ${latest.status.toLowerCase()}.` : 'No Quote yet on this Job.'}
          </p>
          <div className="flex items-center gap-3">
            {error && <span className="text-xs text-error">{error}</span>}
            <button onClick={handleCreate} disabled={busy} className="bg-primary text-on-primary px-4 py-1.5 rounded-lg text-sm font-label-bold hover:bg-primary/90 disabled:opacity-50">
              {latest ? 'Create Revised Quote' : 'Create Quote'}
            </button>
          </div>
        </div>
      );
    }

    if (latest.status === 'Draft') {
      return <QuoteBuilder quote={latest} onSaved={refresh} onSent={refresh} />;
    }

    return (
      <div className="p-4 bg-surface-container-low border-b border-outline-variant space-y-2">
        <QuoteSummary quote={latest} />
        {latest.status === 'Sent' && (
          <div className="flex items-center gap-3">
            {error && <span className="text-xs text-error">{error}</span>}
            <button onClick={handleWithdraw} disabled={busy} className="text-sm text-outline hover:text-error disabled:opacity-50">
              Withdraw Quote
            </button>
          </div>
        )}
      </div>
    );
  }

  // Client side
  if (!latest || latest.status === 'Draft') return null;

  return (
    <div className="p-4 bg-surface-container-low border-b border-outline-variant space-y-2">
      <QuoteSummary quote={latest} />
      {latest.status === 'Sent' && (
        <div className="space-y-2">
          {showDeclineInput && (
            <textarea
              className="w-full text-sm p-2 rounded border border-outline-variant bg-surface"
              placeholder="Reason (optional)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={2}
            />
          )}
          <div className="flex items-center gap-3">
            {error && <span className="text-xs text-error">{error}</span>}
            <button onClick={handleAccept} disabled={busy} className="bg-primary text-on-primary px-4 py-1.5 rounded-lg text-sm font-label-bold hover:bg-primary/90 disabled:opacity-50">
              Accept Quote
            </button>
            {!showDeclineInput ? (
              <button onClick={() => setShowDeclineInput(true)} disabled={busy} className="text-sm text-outline hover:text-error disabled:opacity-50">
                Decline
              </button>
            ) : (
              <button onClick={handleDecline} disabled={busy} className="text-sm text-error hover:underline disabled:opacity-50">
                Confirm decline
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotePanel;
